import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

const RELOAD_FLAG = "moveload.chunk-reload";

/** True for errors caused by an outdated page asking for files a newer release replaced. */
export function isStaleBundleError(err: unknown): boolean {
  const msg = String((err as Error)?.message ?? err ?? "");
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk/i.test(msg);
}

/** Reload once to pick up the latest release; never loop. */
export function reloadForNewRelease(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch { /* storage unavailable — still try once */ }
  window.location.reload();
  return true;
}

interface State { error: Error | null }

/**
 * Catches render errors anywhere below it so a failure shows a recoverable
 * message instead of a blank white page.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("App crashed:", error);
    if (isStaleBundleError(error)) reloadForNewRelease();
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl font-semibold text-foreground">This page didn't load properly</h1>
          <p className="text-sm text-muted-foreground">
            Moveload may have just been updated. Reload to get the latest version — anything you'd typed into a booking is saved.
          </p>
          <p className="text-xs text-muted-foreground break-words">Details: {this.state.error.message}</p>
          <div className="flex justify-center gap-2">
            <Button onClick={() => window.location.reload()}>Reload</Button>
            <Button variant="outline" onClick={() => { window.location.href = "/app"; }}>Go to my dashboard</Button>
          </div>
        </div>
      </div>
    );
  }
}
